/*
  Drives snippets/nav-panels-mobile.liquid.

  Deliberately small. All panels are server-rendered, so this owns state only:
  which panel is active, and what to focus. There is no templating, no height
  measurement, no scroll or resize handler, and no rAF loop — every visual
  change is a data attribute the stylesheet reacts to. That keeps the main
  thread free during the transition, which is where mobile menus usually drop
  frames.

  One delegated listener per component rather than one per row: the menu has
  ~90 links, and binding each would cost more than the whole rest of this file.
*/
(() => {
  const TAG = 'mobile-nav-panels';
  if (customElements.get(TAG)) return;

  const PanelStateEnum = {
    Active: 'active',
    Parent: 'parent',
  };

  class MobileNavPanels extends HTMLElement {
    connectedCallback() {
      this.panels = new Map();
      this.querySelectorAll('[data-mnav-panel]').forEach((el) => {
        this.panels.set(el.dataset.mnavPanel, el);
      });

      // Child panels ship inside <template> and become real DOM the first time
      // they are opened. Parsed but inert, they cost no nodes, no layout, and
      // no image fetches until then.
      this.templates = new Map();
      this.querySelectorAll('[data-mnav-template]').forEach((el) => {
        this.templates.set(el.dataset.mnavTemplate, el);
      });

      this.rootId = this.dataset.mnavRoot || 'root';
      // Nothing to drive without a root; leave the CSS no-JS baseline in place.
      if (!this.panels.has(this.rootId)) return;

      this.stack = [this.rootId];
      this.triggers = new Map();

      // Which groups shipped collapsed, so reset() restores that rather than
      // expanding everything. Server-rendered state is the source of truth.
      //
      // Exception: on a short drawer the one open department fills the view and
      // pushes the rest below the fold, which is the problem leaving them open
      // was meant to avoid. Below this height every section starts closed so all
      // of them are visible at once. Read once, not on resize — the drawer is
      // not a layout that people resize mid-use.
      const SHORT_VIEWPORT = 640;
      const collapseAll = window.innerHeight < SHORT_VIEWPORT;

      this.initiallyCollapsed = new Set();
      this.querySelectorAll('[data-mnav-group]').forEach((group, i) => {
        if (collapseAll || group.hasAttribute('data-mnav-collapsed')) this.initiallyCollapsed.add(i);
      });
      if (collapseAll) this.restoreGroups();

      this.handleClick = this.handleClick.bind(this);
      this.handleKeyup = this.handleKeyup.bind(this);
      this.reset = this.reset.bind(this);

      this.addEventListener('click', this.handleClick);
      // keyup, not keydown: drawer-element closes itself on Escape via a keyup
      // listener on the drawer, which is an ancestor of this element. Binding
      // keydown here meant stopPropagation applied to a different event and the
      // drawer still closed from three levels deep.
      this.addEventListener('keyup', this.handleKeyup);
      document.addEventListener('theme:drawer:close', this.reset);

      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:drawer:close', this.reset);
    }

    handleClick(event) {
      const toggle = event.target.closest('[data-mnav-toggle]');
      if (toggle) {
        this.toggleGroup(toggle);
        return;
      }

      const drill = event.target.closest('[data-mnav-open]');
      if (drill) {
        event.preventDefault();
        this.open(drill.dataset.mnavOpen, drill);
        return;
      }

      const back = event.target.closest('[data-mnav-back]');
      if (back) {
        event.preventDefault();
        this.back();
      }
    }

    /*
      Escape steps back one level while there is somewhere to step back to, and
      otherwise falls through to the drawer's own close handler. Without the
      stopPropagation the first Escape would close the whole drawer from three
      levels deep, losing the shopper's place entirely.
    */
    handleKeyup(event) {
      if (event.key !== 'Escape' || this.stack.length < 2) return;
      event.preventDefault();
      event.stopPropagation();
      this.back();
    }

    /*
      Collapse a department. State lives on the group element and the CSS does
      the rest, so this stays a single attribute write with no measurement.
      Groups start expanded and are not remembered between opens — the menu
      resets to root on close, and a half-collapsed menu on reopen would be a
      state the shopper has to re-read rather than recognise.
    */
    toggleGroup(toggle) {
      const group = toggle.closest('[data-mnav-group]');
      if (!group) return;

      const collapsed = group.hasAttribute('data-mnav-collapsed');
      if (collapsed) group.removeAttribute('data-mnav-collapsed');
      else group.setAttribute('data-mnav-collapsed', '');
      toggle.setAttribute('aria-expanded', String(collapsed));
    }

    /*
      The panel for `id`, cloning it out of its <template> on first use. Kept
      once created: a shopper who opens Boots is likely to open it again, and
      re-cloning would drop scroll position and re-decode the images.
    */
    materialize(id) {
      const existing = this.panels.get(id);
      if (existing) return existing;

      const template = this.templates.get(id);
      if (!template) return null;

      const fragment = template.content.cloneNode(true);
      const panel = fragment.querySelector('[data-mnav-panel]');
      if (!panel) return null;

      // inert before insertion, so it cannot take focus in the frame between
      // being added and sync() running.
      panel.inert = true;
      this.appendChild(fragment);
      this.panels.set(id, panel);
      return panel;
    }

    open(id, trigger) {
      const panel = this.materialize(id);
      if (!panel || this.stack[this.stack.length - 1] === id) return;

      this.triggers.set(id, trigger);
      this.stack.push(id);
      this.sync();

      panel.scrollTop = 0;
      const back = panel.querySelector('[data-mnav-back]');
      if (back) back.focus({ preventScroll: true });
    }

    back() {
      if (this.stack.length < 2) return;

      const leaving = this.stack.pop();
      this.sync();

      // Return focus to the chevron that opened the panel, not the top of the
      // list, so keyboard and screen-reader users resume where they were.
      const trigger = this.triggers.get(leaving);
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    }

    /* Put every group back to the state it started in. */
    restoreGroups() {
      this.querySelectorAll('[data-mnav-group]').forEach((group, i) => {
        const shouldCollapse = this.initiallyCollapsed.has(i);
        if (shouldCollapse) group.setAttribute('data-mnav-collapsed', '');
        else group.removeAttribute('data-mnav-collapsed');
        const toggle = group.querySelector('[data-mnav-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(!shouldCollapse));
      });
    }

    /* Drawer closed: rewind to root and restore the shipped open/closed state. */
    reset() {
      this.restoreGroups();

      if (this.stack.length < 2) return;
      this.stack = [this.rootId];
      this.sync();
    }

    sync() {
      const active = this.stack[this.stack.length - 1];
      const inPath = new Set(this.stack);

      this.panels.forEach((el, id) => {
        const isActive = id === active;
        const state = isActive ? PanelStateEnum.Active : inPath.has(id) ? PanelStateEnum.Parent : '';

        if (state) el.setAttribute('data-mnav-state', state);
        else el.removeAttribute('data-mnav-state');

        // inert covers the transition window, when a panel is still painted but
        // must not take focus. visibility:hidden alone leaves a gap.
        el.inert = !isActive;
      });

      this.dataset.mnavDepth = String(this.stack.length - 1);
    }
  }

  customElements.define(TAG, MobileNavPanels);
})();
